// =============================================================================
// Admin Roles Page — List and manage admin roles
// =============================================================================

import { useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Card,
  CardHeader,
  Button,
  Badge,
  Input,
  Spinner,
  Table,
  Modal,
  ModalFooter,
} from '@tkpod/ui';
import type { Column } from '@tkpod/ui';
import { api } from '@/lib/api';
import type { AdminRoleSummary } from '@tkpod/shared';

// ---------------------------------------------------------------------------
// API response types
// ---------------------------------------------------------------------------

interface RolesListResponse {
  data: AdminRoleSummary[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function AdminRolesPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // -- Local state ----------------------------------------------------------
  const [search, setSearch] = useState('');
  const [deleteTarget, setDeleteTarget] = useState<AdminRoleSummary | null>(null);

  // -- Query ----------------------------------------------------------------
  const rolesQuery = useQuery<RolesListResponse>({
    queryKey: ['admin-roles'],
    queryFn: () => api.get<RolesListResponse>('/api/v1/admin/roles'),
  });

  // -- Delete mutation ------------------------------------------------------
  const deleteMutation = useMutation({
    mutationFn: (id: string) =>
      api.delete<{ message: string }>(`/api/v1/admin/roles/${id}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin-roles'] });
      setDeleteTarget(null);
    },
  });

  // -- Filtered data --------------------------------------------------------
  const roles = rolesQuery.data?.data ?? [];
  const filteredRoles = useMemo(() => {
    if (!search.trim()) return roles;
    const q = search.toLowerCase();
    return roles.filter(
      (r) =>
        r.name.toLowerCase().includes(q) ||
        r.slug.toLowerCase().includes(q) ||
        (r.description?.toLowerCase().includes(q) ?? false),
    );
  }, [roles, search]);

  // -- Table columns --------------------------------------------------------
  const columns: Column<AdminRoleSummary>[] = useMemo(
    () => [
      {
        key: 'name',
        header: 'Name',
        render: (role) => (
          <button
            type="button"
            onClick={() => navigate(`/roles/${role.id}`)}
            className="font-medium text-brand-400 hover:text-brand-300 hover:underline"
          >
            {role.name}
          </button>
        ),
      },
      {
        key: 'slug',
        header: 'Slug',
        render: (role) => (
          <code className="rounded bg-surface-tertiary px-1.5 py-0.5 text-xs text-text-secondary">
            {role.slug}
          </code>
        ),
      },
      {
        key: 'isSystemRole',
        header: 'Type',
        render: (role) =>
          role.isSystemRole ? (
            <Badge variant="warning" size="sm">
              System
            </Badge>
          ) : (
            <Badge variant="default" size="sm">
              Custom
            </Badge>
          ),
      },
      {
        key: 'permissionCount',
        header: 'Permissions',
        render: (role) => (
          <span className="text-text-secondary">{role.permissionCount}</span>
        ),
      },
      {
        key: 'userCount',
        header: 'Users',
        render: (role) => (
          <span className="text-text-secondary">{role.userCount}</span>
        ),
      },
      {
        key: 'actions',
        header: 'Actions',
        render: (role) => (
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/roles/${role.id}`);
              }}
            >
              View
            </Button>
            {!role.isSystemRole && (
              <>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/roles/${role.id}/edit`);
                  }}
                >
                  Edit
                </Button>
                {role.userCount > 0 ? (
                  <span className="inline-flex items-center gap-1 px-3 py-1.5 text-xs text-text-tertiary">
                    <svg className="h-3.5 w-3.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 15v.01M12 9v3m0 8a9 9 0 110-18 9 9 0 010 18z" />
                    </svg>
                    {role.userCount} user{role.userCount !== 1 ? 's' : ''} assigned
                  </span>
                ) : (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      deleteMutation.reset();
                      setDeleteTarget(role);
                    }}
                    className="!text-red-400 hover:!text-red-300"
                  >
                    Delete
                  </Button>
                )}
              </>
            )}
          </div>
        ),
      },
    ],
    [navigate, deleteMutation],
  );

  // -- Cancel delete handler ------------------------------------------------
  const cancelDelete = useCallback(() => {
    setDeleteTarget(null);
    deleteMutation.reset();
  }, [deleteMutation]);

  // -- Render ---------------------------------------------------------------
  if (rolesQuery.isLoading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Spinner size="lg" label="Loading roles..." />
      </div>
    );
  }

  if (rolesQuery.isError) {
    return (
      <Card>
        <div className="flex flex-col items-center justify-center gap-4 py-16">
          <p className="text-sm text-red-400">
            Failed to load roles: {rolesQuery.error?.message ?? 'Unknown error'}
          </p>
          <Button
            variant="outline"
            size="sm"
            onClick={() => void rolesQuery.refetch()}
          >
            Retry
          </Button>
        </div>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card>
        <CardHeader
          title="Admin Roles"
          description="Manage roles and their permissions for admin users."
          action={
            <Button size="sm" onClick={() => navigate('/roles/new')}>
              + Create Role
            </Button>
          }
        />

        {/* Search */}
        <div className="mt-4 max-w-sm">
          <Input
            placeholder="Search roles..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        {/* Table */}
        <div className="mt-4">
          <Table<AdminRoleSummary>
            columns={columns}
            data={filteredRoles}
            rowKey={(role) => role.id}
            emptyMessage={
              search
                ? 'No roles match your search.'
                : 'No roles found. Create your first role.'
            }
          />
        </div>
      </Card>

      {/* Delete confirmation modal */}
      <Modal
        open={!!deleteTarget}
        onClose={cancelDelete}
        title="Delete Role"
        description={`Are you sure you want to delete the role "${deleteTarget?.name ?? ''}"? This action cannot be undone.`}
        size="sm"
      >
        {deleteMutation.isError && (
          <div className="mb-4 rounded-md bg-red-500/10 px-4 py-3 text-sm text-red-400">
            {deleteMutation.error?.message ?? 'Failed to delete role.'}
          </div>
        )}

        <ModalFooter>
          <Button
            variant="ghost"
            onClick={cancelDelete}
            disabled={deleteMutation.isPending}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            loading={deleteMutation.isPending}
            onClick={() => {
              if (deleteTarget) {
                deleteMutation.mutate(deleteTarget.id);
              }
            }}
          >
            Delete Role
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
}
